"use client";

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { useContent } from "@/components/site/ContentProvider";
import { cn } from "@/lib/utils";
import type { ContactCurrency } from "@/lib/phone-countries";

type Currency = ContactCurrency;

/**
 * Contact currency picker — portals the panel to body so mobile
 * `#contact` / `.budget_set` overflow cannot clip options.
 */
export function CurrencySelect({
  name = "currency",
  value,
  onCurrencyChange,
  className,
}: {
  name?: string;
  value: Currency;
  onCurrencyChange?: (currency: Currency) => void;
  className?: string;
}) {
  const site = useContent();
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({});
  const [mounted, setMounted] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    setMounted(true);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;

    const place = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const gap = 6;
      /* Match country panel roomier rows — slightly taller scroll area */
      const maxH = Math.min(window.innerHeight * 0.5, 16 * 16);
      const spaceBelow = window.innerHeight - rect.bottom - gap - 8;
      const spaceAbove = rect.top - gap - 8;
      const placeTop =
        spaceBelow < Math.min(maxH, 8 * 16) && spaceAbove > spaceBelow;
      const height = Math.min(maxH, Math.max(placeTop ? spaceAbove : spaceBelow, 6 * 16));
      const width = Math.max(rect.width, 5.75 * 16);
      const left = Math.min(
        Math.max(8, rect.left),
        window.innerWidth - width - 8,
      );

      setPanelStyle({
        position: "fixed",
        left,
        width,
        maxHeight: height,
        zIndex: 10050,
        ...(placeTop
          ? { bottom: window.innerHeight - rect.top + gap, top: "auto" }
          : { top: rect.bottom + gap, bottom: "auto" }),
      });
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function pick(code: Currency) {
    onCurrencyChange?.(code);
    setOpen(false);
  }

  function onTriggerKey(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  }

  const panel =
    open && mounted
      ? createPortal(
          <div
            ref={panelRef}
            id={listId}
            role="listbox"
            aria-label={site.contact.form.currencyPlaceholder}
            style={panelStyle}
            className={cn(
              "budget_currency_panel",
              "overflow-y-auto rounded-none border border-white/20 bg-[#080808] text-white shadow-none",
            )}
            data-native-scroll=""
          >
            <ul className="m-0 list-none p-0">
              {site.contact.currencies.map((code) => {
                const selected = code === value;
                return (
                  <li key={code} className="m-0 p-0">
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={cn(
                        "budget_currency_option flex w-full items-center px-3.5 py-3 text-start text-[1em] uppercase tracking-[0.06em] text-white/70",
                        "hover:bg-white/10 hover:text-white",
                        selected && "bg-white/10 text-white",
                      )}
                      onClick={() => pick(code as Currency)}
                    >
                      {code}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={rootRef}
      className={cn("budget_currency", className)}
      dir="ltr"
    >
      <input type="hidden" name={name} value={value} />
      <button
        ref={triggerRef}
        type="button"
        className={cn(
          "budget_currency_trigger",
          "flex h-[2.25em] min-w-[5.75em] items-center justify-between gap-2 rounded-none border border-white/20 bg-transparent px-3 py-0",
          "text-[0.875em] uppercase tracking-[0.06em] text-white shadow-none",
          "hover:border-white/45",
          open && "border-white/45",
        )}
        aria-label={`${site.contact.form.currencyPlaceholder}, ${value}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKey}
      >
        <span className="label small text-white">{value}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 opacity-70 transition-transform",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      {panel}
    </div>
  );
}
